/* bs-history — slider mobile de hitos cronologicos.
   Patron tomado de bs-testimonials.js: scrollByCard explicito + dots con click → scrollToIndex
   + active dot via aria-current basado en scrollLeft.
   Re-init en shopify:section:load (customizer). */

(function () {
  'use strict';

  class BsHistory {
    constructor(section) {
      this.section = section;
      this.scroller = section.querySelector('[data-bs-history-scroller]');
      this.list = this.scroller ? this.scroller.querySelector('.bs-history__list') : null;
      this.prevBtns = Array.from(section.querySelectorAll('[data-direction="prev"]'));
      this.nextBtns = Array.from(section.querySelectorAll('[data-direction="next"]'));
      this.dotsContainer = section.querySelector('[data-bs-history-dots]');
      this.dots = this.dotsContainer
        ? Array.from(this.dotsContainer.querySelectorAll('[data-bs-history-dot]'))
        : [];
      this.cards = this.scroller ? this.scroller.querySelectorAll('.bs-history__item') : [];
      this.totalCards = this.cards.length;

      if (!this.scroller || this.totalCards === 0) {
        return;
      }

      this.bindEvents();
      this.checkOverflow();
      this.updateUi();
    }

    bindEvents() {
      this.prevBtns.forEach((btn) => btn.addEventListener('click', () => this.scrollByCard(-1)));
      this.nextBtns.forEach((btn) => btn.addEventListener('click', () => this.scrollByCard(1)));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.bsHistoryDot, 10);
          if (Number.isFinite(idx)) this.scrollToIndex(idx);
        });
      });

      this.scroller.addEventListener('scroll', () => this.updateUi(), { passive: true });

      window.addEventListener(
        'resize',
        () => {
          this.checkOverflow();
          this.updateUi();
        },
        { passive: true }
      );
    }

    /* Detecta overflow real (hay mas cards de las que caben en el scroller).
       Mobile: siempre overflow (1 card visible vs 4 hitos).
       Desktop (Rafa con grid 4 cols): no hay overflow → atributo se quita.
       JS valida ambos casos sin asumir viewport. */
    checkOverflow() {
      if (!this.scroller) return;
      const tolerance = 1;
      const isOverflowing = this.scroller.scrollWidth > this.scroller.clientWidth + tolerance;
      if (isOverflowing) {
        this.scroller.dataset.overflow = 'true';
      } else {
        this.scroller.removeAttribute('data-overflow');
      }
    }

    getStep() {
      /* Step = card width + gap. Lee del DOM por si cambia con resize. */
      if (!this.cards[0]) return 0;
      const cardWidth = this.cards[0].offsetWidth;
      let gap = 16;
      if (this.list) {
        const computed = window.getComputedStyle(this.list);
        const parsedGap = parseFloat(computed.gap || computed.columnGap);
        if (!Number.isNaN(parsedGap)) gap = parsedGap;
      }
      return cardWidth + gap;
    }

    scrollByCard(direction) {
      const step = this.getStep();
      if (step <= 0) return;

      const prefersReduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = prefersReduced ? 'auto' : 'smooth';

      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      const currentScrollLeft = this.scroller.scrollLeft;

      let target = direction > 0 ? currentScrollLeft + step : currentScrollLeft - step;

      target = Math.max(0, Math.min(target, maxScroll));
      this.scroller.scrollTo({ left: target, behavior });
    }

    scrollToIndex(idx) {
      const step = this.getStep();
      if (step <= 0) return;
      const prefersReduced =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = prefersReduced ? 'auto' : 'smooth';

      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
      const target = Math.max(0, Math.min(idx * step, maxScroll));
      this.scroller.scrollTo({ left: target, behavior });
    }

    /**
     * Calcula el indice del card "activo" basado en scrollLeft.
     * Returns entero 0..totalCards-1.
     */
    getActiveIndex() {
      const step = this.getStep();
      if (step <= 0) return 0;
      const scrollLeft = this.scroller.scrollLeft;
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;

      /* Edge case: ultimo card alineado a end */
      if (scrollLeft >= maxScroll - 2 && maxScroll > 0) {
        return this.totalCards - 1;
      }
      const idx = Math.round(scrollLeft / step);
      return Math.max(0, Math.min(idx, this.totalCards - 1));
    }

    updateUi() {
      const scrollLeft = this.scroller.scrollLeft;
      const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;

      const prevDisabled = scrollLeft < 1;
      this.prevBtns.forEach((btn) => {
        btn.disabled = prevDisabled;
        btn.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
      });
      const nextDisabled = scrollLeft >= maxScroll - 1;
      this.nextBtns.forEach((btn) => {
        btn.disabled = nextDisabled;
        btn.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
      });

      /* Dot activo: aria-current="true" + clase modifier */
      if (this.dots.length > 0) {
        const activeIdx = this.getActiveIndex();
        this.dots.forEach((dot, i) => {
          const isActive = i === activeIdx;
          dot.classList.toggle('bs-history__dot--active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }
  }

  function init(root) {
    const sections = (root || document).querySelectorAll('.bs-history');
    sections.forEach((section) => {
      if (section.__bsHistoryInit) return;
      section.__bsHistoryInit = true;
      new BsHistory(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  /* Re-init cuando el customizer recarga la section */
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.bs-history');
    if (section) {
      section.__bsHistoryInit = false;
      init(event.target);
    }
  });
})();
